import React from 'react';
import { Box, Typography, Paper, Alert } from '@mui/material';
import { makeStyles, Theme } from '@material-ui/core/styles';

import MultipleChoiceSelector from '../../../../../components/ImpactAiDrawer/components/MultipleChoiceSelector';
import { ImpactAiContextProvider } from '../../../../../components/ImpactAiDrawer/hooks/ImpactAiContextProvider';
import { MultipleChoiceOption } from '../../../types';

interface MultipleChoicePreviewSectionProps {
  choices: MultipleChoiceOption[];
  minSelections?: number;
  maxSelections?: number;
  submitButtonText?: string;
}

// Replicate the chat styles for proper preview context
const usePreviewStyles = makeStyles((theme: Theme) => ({
  chatPreviewContainer: {
    backgroundColor: '#f5f5f5',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
    minHeight: 200,
    maxWidth: 600,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  messageWrapper: {
    display: 'flex',
    width: '100%',
    margin: theme.spacing(1, 0),
  },
  botMessage: {
    justifyContent: 'flex-start',
  },
  botMessageBubble: {
    backgroundColor: '#F4F4F6',
    color: theme.palette.text.primary,
    border: 'none',
    borderRadius: `${theme.spacing(2)}px ${theme.spacing(2)}px ${theme.spacing(2)}px 0`,
    padding: theme.spacing(1, 2),
    display: 'inline-block',
    maxWidth: '80%',
    wordBreak: 'break-word',
  },
  // Inline multiple choice selector styles from the actual chat
  inlineMultipleChoiceWrapper: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    margin: '0 auto',
    maxWidth: '90%',
    position: 'relative',
  },
  inlineMultipleChoiceContainer: {
    padding: `${theme.spacing(1)}px 0`,
    maxWidth: '100%',
    position: 'relative',
  },
}));

const MultipleChoicePreviewSection: React.FC<MultipleChoicePreviewSectionProps> = ({
  choices,
  minSelections,
  maxSelections,
  submitButtonText,
}) => {
  const classes = usePreviewStyles();

  const hasChoices = choices.length > 0;
  const hasValidChoices = choices.every((choice) => choice.text && choice.value);

  if (!hasChoices) {
    return <Alert severity="info">Add choices to see a preview</Alert>;
  }

  if (!hasValidChoices) {
    return (
      <Alert severity="warning">
        All choices must have both display text and value to show preview
      </Alert>
    );
  }

  // Convert our MultipleChoiceOption array to ChoiceOption array expected by MultipleChoiceSelector
  const formattedChoices = choices.map((choice) => ({
    text: choice.text,
    value: choice.value,
  }));

  const configSummary = [];
  if (minSelections !== undefined) {
    configSummary.push(`Min: ${minSelections}`);
  }
  if (maxSelections !== undefined) {
    configSummary.push(`Max: ${maxSelections}`);
  }
  if (submitButtonText) {
    configSummary.push(`Button: "${submitButtonText}"`);
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Preview
      </Typography>

      <Paper elevation={2} className={classes.chatPreviewContainer}>
        {/* Bot message introducing the multiple choice */}
        <div className={classes.messageWrapper}>
          <div className={classes.botMessage}>
            <div className={classes.botMessageBubble}>Select one or more options:</div>
          </div>
        </div>

        {/* Multiple choice selector */}
        <div className={classes.messageWrapper}>
          <div className={classes.inlineMultipleChoiceWrapper}>
            <ImpactAiContextProvider initialContext={{}}>
              <MultipleChoiceSelector
                choices={formattedChoices}
                onSubmit={(selectedChoices) => {
                  // eslint-disable-next-line no-console
                  console.log('Preview selection:', selectedChoices);
                }}
                className={classes.inlineMultipleChoiceContainer}
                inline
                minSelections={minSelections}
                maxSelections={maxSelections}
                submitButtonText={submitButtonText}
              />
            </ImpactAiContextProvider>
          </div>
        </div>
      </Paper>

      <Box mt={1}>
        <Typography variant="caption" color="textSecondary" display="block">
          This is how the multiple choice appears in the chat interface
        </Typography>
        {configSummary.length > 0 && (
          <Typography variant="caption" color="textSecondary" display="block">
            Configuration: {configSummary.join(', ')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default MultipleChoicePreviewSection;
